/* eslint-disable max-lines-per-function -- Large component */
import { CHARACTER_MODERATION_STATUS, CharacterType, ICharacter } from "@next-platform/client-common-core";
import { CharacterSortOrder } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "use-debounce";

import { FilterKeys, SortOptions } from "@/components/type";
import { filterAndSortResults } from "@/utils/filterAndSortResults";

import { useMyChatbots } from "./useMyChatbots";

interface IUseMyChatbotsDataProps {
  initialSortType?: string;
  type?: CharacterType;
  includeAttachedLorebookId?: boolean;
  // When false, the underlying fetch is skipped entirely. Used to avoid loading
  // a character type (e.g. groups) that a feature flag has turned off.
  isFetchEnabled?: boolean;
}

interface IUseMyChatbotsDataReturn {
  chatbots: ICharacter[] | null;
  filteredAndSortedChatbots: ICharacter[] | null;
  isLoading: boolean;
  isFiltering: boolean;
  isLoadingMoreGroupchats: boolean;
  shouldShowLoadMoreButton: boolean;
  error: Error | null;
  localSortType: string;
  localSearchQuery: string;
  localFilterBy: FilterKeys;
  localFilterValue: string;
  handleLocalSort: (sortType: string) => void;
  handleLocalSearch: (query: string) => void;
  handleLocalFilter: (filterKey: FilterKeys, filterValue: string) => void;
  refetch: () => Promise<void>;
  loadMoreChatbots: () => void;
}

const getModerationOrder = (chatItem: ICharacter): number => {
  const status = chatItem.moderationReport?.moderation_status;
  if (status === CHARACTER_MODERATION_STATUS.FIX_REQUIRED) return 1;
  if (status === CHARACTER_MODERATION_STATUS.REREVIEW_REQUIRED || status === CHARACTER_MODERATION_STATUS.PENDING) return 2;
  // eslint-disable-next-line no-magic-numbers -- 3 is the default sort order for other statuses
  return 3;
};

const sortByModerationStatus = (items: ICharacter[]): ICharacter[] =>
  [...items].sort((chatItemA, chatItemB) => getModerationOrder(chatItemA) - getModerationOrder(chatItemB));

const sortTypeMap: Record<string, SortOptions> = {
  [CharacterSortOrder.Latest]: SortOptions.LATEST,
  [CharacterSortOrder.Oldest]: SortOptions.OLDEST,
  [CharacterSortOrder.TopRated]: SortOptions.TOP_RATED,
  [CharacterSortOrder.Name]: SortOptions.NAME,
};

const SERVER_PARAMS_DEBOUNCE_MS = 500;

export const useMyChatbotsData = ({
  initialSortType = "latest",
  type,
  includeAttachedLorebookId,
  isFetchEnabled = true,
}: IUseMyChatbotsDataProps = {}): IUseMyChatbotsDataReturn => {
  const {
    chatbots: myChatbotsData,
    isLoading,
    isLoadingMoreGroupchats,
    chatbotsLastKey,
    error,
    loadMoreGroupchats,
    refetchGroupchatsWith,
  } = useMyChatbots({ type, includeAttachedLorebookId, isFetchEnabled });

  const [stateLocalSortType, setStateLocalSortType] = useState(initialSortType);
  const [stateLocalSearchQuery, setStateLocalSearchQuery] = useState("");
  const [stateLocalFilterBy, setStateLocalFilterBy] = useState<FilterKeys>(FilterKeys.ALL);
  const [stateLocalFilterValue, setStateLocalFilterValue] = useState("All");

  const [debouncedSearchQuery] = useDebounce(stateLocalSearchQuery, SERVER_PARAMS_DEBOUNCE_MS);
  const isFiltering = stateLocalSearchQuery !== debouncedSearchQuery;

  const serverFilter = stateLocalFilterBy !== FilterKeys.ALL ? stateLocalFilterValue : undefined;

  const handleLocalSort = useCallback((sortType: string) => setStateLocalSortType(sortType), []);
  const handleLocalSearch = useCallback((query: string) => setStateLocalSearchQuery(query), []);
  const handleLocalFilter = useCallback((filterKey: FilterKeys, filterValue: string) => {
    setStateLocalFilterBy(filterKey);
    setStateLocalFilterValue(filterValue);
  }, []);

  const refRefetchGroupchatsWith = useRef(refetchGroupchatsWith);
  refRefetchGroupchatsWith.current = refetchGroupchatsWith;

  const refCurrentParams = useRef({ sort: stateLocalSortType, search: debouncedSearchQuery, filter: serverFilter });
  refCurrentParams.current = { sort: stateLocalSortType, search: debouncedSearchQuery, filter: serverFilter };

  const refIsServerModeDetermined = useRef(false);
  const refIsServerMode = useRef<boolean | null>(null);
  const [stateIsServerMode, setStateIsServerMode] = useState<boolean | null>(null);

  useEffect(() => {
    if (chatbotsLastKey !== null) {
      // If we receive a lastKey, we must irrevocably lock into Server Mode.
      // This overrides any premature client-side locks caused by missing userId.
      const wasServerMode = refIsServerMode.current === true;
      refIsServerModeDetermined.current = true;
      refIsServerMode.current = true;
      setStateIsServerMode(true);
      // Re-apply non-default params whenever server mode locks without having been server
      // mode before — covers the undetermined first lock and a client-mode → server-mode
      // switch (e.g. userId arrived late). Later lastKey updates skip this since the mode
      // is already server.
      const params = refCurrentParams.current;
      const hasNonDefaultParams =
        !wasServerMode && (params.sort !== CharacterSortOrder.Latest || params.search !== "" || params.filter !== undefined);
      if (hasNonDefaultParams) {
        void refRefetchGroupchatsWith.current(params);
      }
    } else if (!refIsServerModeDetermined.current && !isLoading && myChatbotsData !== null) {
      // If we finished loading data and still have no lastKey, lock to client mode.
      refIsServerModeDetermined.current = true;
      refIsServerMode.current = false;
      setStateIsServerMode(false);
    }
  }, [isLoading, myChatbotsData, chatbotsLastKey]);

  const refetch = useCallback(async () => {
    if (refIsServerMode.current === true) {
      await refRefetchGroupchatsWith.current(refCurrentParams.current);
    } else {
      await refRefetchGroupchatsWith.current({});
    }
  }, []);

  const refIsFirstServerRun = useRef(true);
  useEffect(() => {
    if (refIsFirstServerRun.current) {
      refIsFirstServerRun.current = false;
      // On the first run, the initial fetch already happened with empty params (server defaults).
      // Only refetch if the initial local params diverge from those defaults (e.g. Redux-persisted sort).
      const hasNonDefaultParams = stateLocalSortType !== CharacterSortOrder.Latest || debouncedSearchQuery !== "" || serverFilter !== undefined;
      if (!hasNonDefaultParams) return;
    }
    if (refIsServerMode.current === true) {
      void refRefetchGroupchatsWith.current({ sort: stateLocalSortType, search: debouncedSearchQuery, filter: serverFilter });
    }
  }, [stateLocalSortType, debouncedSearchQuery, serverFilter]);

  const filteredAndSortedChatbots = useMemo(() => {
    if (!myChatbotsData || stateIsServerMode === null) return null;
    if (stateIsServerMode !== false) {
      return sortByModerationStatus(myChatbotsData);
    }
    const sortValue = sortTypeMap[stateLocalSortType] || SortOptions.LATEST;
    const sorted = filterAndSortResults(myChatbotsData, debouncedSearchQuery, sortValue, stateLocalFilterBy, stateLocalFilterValue);
    return sortByModerationStatus(sorted);
  }, [myChatbotsData, stateIsServerMode, debouncedSearchQuery, stateLocalSortType, stateLocalFilterBy, stateLocalFilterValue]);

  const loadMoreChatbots = useCallback(() => {
    loadMoreGroupchats({ sort: stateLocalSortType, search: debouncedSearchQuery, filter: serverFilter });
  }, [loadMoreGroupchats, stateLocalSortType, debouncedSearchQuery, serverFilter]);

  return {
    chatbots: myChatbotsData,
    filteredAndSortedChatbots,
    isLoading,
    isFiltering,
    isLoadingMoreGroupchats,
    shouldShowLoadMoreButton: !!chatbotsLastKey,
    error,
    localSortType: stateLocalSortType,
    localSearchQuery: stateLocalSearchQuery,
    localFilterBy: stateLocalFilterBy,
    localFilterValue: stateLocalFilterValue,
    handleLocalSort,
    handleLocalSearch,
    handleLocalFilter,
    refetch,
    loadMoreChatbots,
  };
};

export default useMyChatbotsData;
